import { useSearchParams } from 'react-router-dom';
import { useGetSearchCandidateProfileQueryLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { convertQueryToTableFilters } from 'modules/candidate-profile/tables/filters/convertQueryToTableFilters';
import { extractStoredFilterTree } from 'modules/candidate-profile/tables/filters/extractStoredFilterTree';
import { SEARCH_QUERY_ID_PARAM } from 'modules/candidate-profile/hooks/useSearchQueryPersistence';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { TableV3Filters } from 'common/table/components/TableV3';
import { CandidateFilterNode } from 'modules/candidate-profile/types/CandidateSearchFilter';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';

export type HandleSelectSearchCandidateProfileQueryProps = {
  searchCandidateProfileQueryId: string;
  filters: TableV3Filters<CandidateProfileTableColumns>;
  /**
   * The tree the search actually ran, recruiter's filters and the agent's
   * combined. Preferred over `filters` when present: the flat columns hold one
   * condition per field and cannot express Include all, Exclude, a geo radius
   * or a custom field. Absent for searches made before the sourcing endpoint.
   */
  filterTree: CandidateFilterNode | null;
};

type UseSelectSearchCandidateProfileQueryProps = {
  onSelectSearchCandidateProfileQuery: (
    data: HandleSelectSearchCandidateProfileQueryProps
  ) => void;
  onSelectCurrent?: () => void;
};

export const useSelectSearchCandidateProfileQuery = ({
  onSelectSearchCandidateProfileQuery,
  onSelectCurrent
}: UseSelectSearchCandidateProfileQueryProps) => {
  const [searchParams] = useSearchParams();
  const [fetchQuery] = useGetSearchCandidateProfileQueryLazyQuery();
  const { showToast } = useToast();

  const currentSearchCandidateProfileQueryId = searchParams.get(
    SEARCH_QUERY_ID_PARAM
  );

  const handleItemClick = async (searchCandidateProfileQueryId: string) => {
    if (
      searchCandidateProfileQueryId === currentSearchCandidateProfileQueryId
    ) {
      onSelectCurrent?.();
      return;
    }

    try {
      const { data } = await fetchQuery({
        variables: { id: searchCandidateProfileQueryId }
      });

      if (!data?.getSearchCandidateProfileQuery) {
        showToast({
          type: ToastType.Error,
          title: 'Failed to load search',
          message: 'Search not found. Please try again.'
        });
        return;
      }

      const filters = convertQueryToTableFilters(data);

      onSelectSearchCandidateProfileQuery({
        searchCandidateProfileQueryId,
        filters: filters || {},
        filterTree: extractStoredFilterTree(data)
      });
    } catch (error) {
      showToast({
        type: ToastType.Error,
        title: 'Failed to load search',
        message: 'Please try again.'
      });
    }
  };

  return { handleItemClick };
};
